import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { near } from '../area-match';
import { epicBackground } from '../epics';
import { useAnchoredPopover } from './useAnchoredPopover';

/**
 * THE EPIC FIELD on a card — the same type-to-create combobox as the Area field,
 * so the two read as one pattern side by side (the whole point of teaching the
 * epic-vs-area difference is that they sit together and behave alike). It replaces
 * the native <input list> + <datalist>, which typed-or-picked but wore the
 * browser's own dropdown — a different control on every platform, with no "No
 * epic" affordance and no near-match nudge.
 *
 * An epic is FREE TEXT, not a stored object, so this is the lighter twin of
 * AreaField: no colour to store and no create call — "create" simply keeps what
 * you typed, and picking an existing epic just reuses its name. The colour dot is
 * the same hashed tint epics wear on the board lanes and chips. A near-match nudge
 * ("Did you mean Q3 migration?") offers a close existing epic before a near-
 * duplicate is made, but never blocks it. Closes on Escape, a pick, or a click
 * away, like every other popover in the app.
 */

interface Props {
  /** The epic name, or '' for "No epic". */
  value: string;
  /** The epics that already exist, to offer for picking. */
  options: string[];
  disabled: boolean;
  onChange: (epic: string) => void;
  /**
   * Whether "No epic" is offered as a choice. True on a card (clearing the epic is
   * valid). False when the field is a REQUIRED target — the move-to picker in the
   * Remove dialog, where "No epic" is not a move at all (that is the Clear option)
   * and offering it is a silent dead end (interaction + ux witnesses, 19 Oct).
   */
  allowNone?: boolean;
  /**
   * Whether the quiet "rename or remove in Settings" pointer shows. True on a card
   * (where cleanup lives elsewhere). False inside the Settings cleanup dialog
   * itself, where the pointer would be self-referential.
   */
  manageHint?: boolean;
  /** Open on mount — the Grid's in-cell editor; omit on the card. */
  autoOpen?: boolean;
  /** Fired when the popover closes, with how (the Grid moves the cell on it). */
  onClose?: (reason: 'pick' | 'cancel' | 'away' | 'next' | 'prev') => void;
}

// The check on the currently-selected epic, so reopening the picker shows which
// one is set — matching the Area field and the colour picker.
function EpicTick() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function EpicField({ value, options, disabled, onChange, allowNone = true, manageHint = true, autoOpen = false, onClose }: Props) {
  const [open, setOpen] = useState(autoOpen);
  const [q, setQ] = useState('');
  const wrap = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const reason = useRef<'pick' | 'cancel' | 'away' | 'next' | 'prev'>('away');
  const wasOpen = useRef(open);
  // Fire onClose once per open→closed transition, however it closed.
  useEffect(() => {
    if (wasOpen.current && !open) onClose?.(reason.current);
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const pos = useAnchoredPopover(open, wrap, pop);
  // Focus the search box only once the popover is on screen — it is rendered
  // visibility:hidden until a position is measured, and focusing a hidden element
  // is silently dropped, which left type-to-find dead until the box was clicked
  // (interaction witness, 17 Oct). The plain boolean flips true the moment the box
  // is focusable and does not re-fire as the popover re-places.
  const visible = open && !!pos;

  useEffect(() => {
    if (visible) input.current?.focus({ preventScroll: true });
  }, [visible]);

  // Close on a click away, or on Escape, like every menu/popover in the app. The
  // popover is portaled outside the field, so let a click inside it (or the field)
  // through. Escape is caught in the document CAPTURE phase and stopped there:
  // when this field is on a card, the card closes on Escape through a window-level
  // (bubble) listener, so the menu's own React handler never beats it and the
  // whole card would drop out of edit instead of the menu closing. Capturing it
  // first gives the open menu first refusal, whatever holds focus (see ColourField).
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrap.current?.contains(t) || pop.current?.contains(t)) return;
      reason.current = 'away';
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        reason.current = 'cancel';
        setOpen(false);
      }
    };
    // Close when focus leaves the control (e.g. Tab-away) so the menu never
    // lingers open out of focus and swallows the next Escape (interaction witness,
    // 17 Oct). Deferred so a WebKit option click — which does not move focus —
    // is not read as leaving.
    const onFocusOut = () => {
      setTimeout(() => {
        const el = document.activeElement;
        // Focus still settling into the just-opened portaled menu (parked on
        // <body> for a tick before the visible-effect moves it in) — not a leave.
        // Closing here would kill the editor before its first key (interaction
        // witness, 19 Oct). A real Tab-away lands on an element, never <body>.
        if (!el || el === document.body) return;
        if (wrap.current?.contains(el) || pop.current?.contains(el)) return;
        reason.current = 'away';
        setOpen(false);
      }, 0);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  const close = (why: 'pick' | 'cancel' | 'next' | 'prev' = 'pick') => {
    reason.current = why;
    setOpen(false);
    setQ('');
  };
  const pick = (epic: string, why: 'pick' | 'next' | 'prev' = 'pick') => {
    onChange(epic);
    close(why);
  };

  const needle = q.trim().toLowerCase();
  const matches = options
    .filter((e) => !needle || e.toLowerCase().includes(needle))
    .sort((a, b) => a.localeCompare(b))
    .slice(0, 8);
  // A create is offered whenever the typed name is not already an epic.
  const exact = options.some((e) => e.toLowerCase().trim() === needle);
  const canCreate = needle.length > 0 && !exact;
  const nearEpic = canCreate ? options.find((e) => near(e, q)) : undefined;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Swallow it so it closes only the popover, the way a native control does —
      // without bubbling up to the card and dropping it out of edit mode.
      e.preventDefault();
      e.stopPropagation();
      close('cancel');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (matches.length) pick(matches[0]);
      else if (canCreate) pick(q.trim());
    } else if (e.key === 'Tab' && onClose) {
      // In the grid, Tab steps to the next cell (Shift+Tab the previous). A query
      // that matches commits the top match like Enter; otherwise the value is left
      // as it is and the cell just moves. preventDefault keeps focus put until the
      // grid re-places it. On the card (no onClose) Tab is left to the browser.
      e.preventDefault();
      e.stopPropagation();
      const dir = e.shiftKey ? 'prev' : 'next';
      if (needle && matches.length) pick(matches[0], dir);
      else close(dir);
    }
  };

  return (
    <span className="area-combo" ref={wrap}>
      <button
        type="button"
        className="ctl quiet area-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Epic"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {value ? (
          <>
            <span className="epic-sw area-trigger-sw" style={{ background: epicBackground(value, options) || 'var(--t3)' }} aria-hidden="true" />
            <span className="area-trigger-name">{value}</span>
          </>
        ) : (
          <span className="area-trigger-name area-trigger-none">{allowNone ? 'No epic' : 'Choose an epic…'}</span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={pop}
          className="sr-ta area-pop"
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            maxHeight: pos?.maxH,
            overflowY: 'auto',
            zIndex: 200,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <input
            ref={input}
            className="ctl sr-ta-input"
            placeholder="Find or create an epic…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <div className="sr-ta-body">
            {nearEpic && (
              <button type="button" className="sr-ta-opt area-near" onClick={() => pick(nearEpic)}>
                <span className="epic-sw" style={{ background: epicBackground(nearEpic, options) || 'var(--t3)' }} aria-hidden="true" />
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-name">Did you mean {nearEpic}?</span>
                  <span className="sr-ta-sub">pick the epic you already have</span>
                </span>
              </button>
            )}

            {allowNone && (
              <button type="button" className="sr-ta-opt" aria-selected={value === ''} onClick={() => pick('')}>
                <span className="epic-sw epics-sw-none" aria-hidden="true" />
                <span className="sr-ta-opt-text"><span className="sr-ta-name">No epic</span></span>
                {value === '' && <EpicTick />}
              </button>
            )}

            {matches.map((e) => (
              <button type="button" className="sr-ta-opt" key={e} aria-selected={e === value} onClick={() => pick(e)}>
                <span className="epic-sw" style={{ background: epicBackground(e, options) || 'var(--t3)' }} aria-hidden="true" />
                <span className="sr-ta-opt-text"><span className="sr-ta-name">{e}</span></span>
                {e === value && <EpicTick />}
              </button>
            ))}

            {canCreate && (
              <button type="button" className="sr-ta-opt area-create" onClick={() => pick(q.trim())}>
                <span className="area-create-plus" aria-hidden="true">+</span>
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-name">Create “{q.trim()}”</span>
                  <span className="sr-ta-sub">a large body of work with an end</span>
                </span>
              </button>
            )}

            {matches.length === 0 && !canCreate && (
              <p className="sr-ta-empty">No epics yet — type a name to create the first.</p>
            )}

            {/* Where an epic is tidied up — renamed or removed — lives in Settings,
                beside Areas and Labels; the card is only where one is added. A quiet
                pointer, not a control, so the picker stays about finding/creating.
                Hidden when the field is itself inside that Settings dialog. */}
            {manageHint && (
              <p className="sr-ta-manage">Rename or remove epics in Settings › Workflow.</p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
